import { OnChangeOrOnBlurType, ViewableResponse } from '../types/global';
import { RootState } from '../types/graph';
import { client } from '../util/client';
import { useUpdateQuestionnaire } from '@/hooks/questionnaire.hooks';
import { parseInlineMarkdown } from '@/util/parseInlineMarkdown';
import axios from 'axios';
import upperFirst from 'lodash/upperFirst';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  MdClose,
  MdImage,
  MdOutlineFileUpload,
  MdPhotoCamera,
  MdPictureAsPdf,
} from 'react-icons/md';

const ALLOWED_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const MAX_SIZE_BYTES = 15 * 1024 * 1024;
const MAX_FILES_PER_SLOT = 10;
// Bound camera captures so a phone photo doesn't upload at full sensor size.
const MAX_IMAGE_EDGE_PX = 2500;
const IMAGE_JPEG_QUALITY = 0.9;

type UploadedFile = { id: string; name: string };
type PendingFile = { tempId: string; name: string };

// Verify the real content type from the file's magic bytes — never trust the
// browser-reported MIME type alone (e.g. a renamed HEIC).
const sniffMimeType = async (file: File): Promise<string | null> => {
  const header = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  const startsWith = (bytes: number[]) =>
    bytes.every((b, i) => header[i] === b);
  if (startsWith([0x25, 0x50, 0x44, 0x46])) return 'application/pdf'; // %PDF
  if (startsWith([0xff, 0xd8, 0xff])) return 'image/jpeg';
  if (startsWith([0x89, 0x50, 0x4e, 0x47])) return 'image/png';
  return null;
};

// Downscale a (camera) image to a sane size; leaves PDFs and already-small
// images untouched. Returns a JPEG blob.
const boundImage = (file: File): Promise<Blob> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const longestEdge = Math.max(img.width, img.height);
      const scale =
        longestEdge > MAX_IMAGE_EDGE_PX ? MAX_IMAGE_EDGE_PX / longestEdge : 1;
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('canvas unavailable'));
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('toBlob failed'))),
        'image/jpeg',
        IMAGE_JPEG_QUALITY,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('image decode failed'));
    };
    img.src = url;
  });

const FileTypeIcon = ({ name }: { name: string }) =>
  name.toLowerCase().endsWith('.pdf') ? (
    <MdPictureAsPdf className="size-5 shrink-0 text-indigo-500" />
  ) : (
    <MdImage className="size-5 shrink-0 text-indigo-500" />
  );

const FileCard = ({
  name,
  onRemove,
}: {
  name: string;
  onRemove?: () => void;
}) => (
  <div className="flex items-center justify-between gap-3 rounded-xl border border-indigo-100 bg-white px-3 py-2 text-sm shadow-[0px_1.758px_7.031px_0px_rgba(0,0,0,0.04)]">
    <div className="flex min-w-0 items-center gap-2">
      <FileTypeIcon name={name} />
      <span className="truncate text-indigo-900">{name}</span>
    </div>
    {onRemove && (
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Retirer ${name}`}
        className="shrink-0 cursor-pointer rounded-full p-1 text-indigo-400 transition-colors hover:bg-indigo-50 hover:text-indigo-700"
      >
        <MdClose className="size-4" />
      </button>
    )}
  </div>
);

const PendingCard = ({ name }: { name: string }) => (
  <div className="flex items-center gap-2 rounded-xl border border-indigo-100 bg-white px-3 py-2 text-sm">
    <span className="size-4 shrink-0 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
    <span className="truncate text-indigo-500">{name}</span>
  </div>
);

export interface ResponseFileUploadFieldProps {
  response: ViewableResponse;
  value?: string;
  disabled?: boolean;
}

export const ResponseFileUploadField = ({
  response,
  value,
  onChange,
  disabled,
}: ResponseFileUploadFieldProps & OnChangeOrOnBlurType<string>) => {
  const questionnaireId = useSelector(
    (state: RootState) => state.questionnaireId,
  );
  const { updateQuestionnaire } = useUpdateQuestionnaire({});
  const persistRequested = useRef(false);
  const documentInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);

  const [files, setFiles] = useState<UploadedFile[]>(() => {
    try {
      return value ? (JSON.parse(value) as UploadedFile[]) : [];
    } catch {
      return [];
    }
  });
  const filesRef = useRef<UploadedFile[]>(
    (() => {
      try {
        return value ? (JSON.parse(value) as UploadedFile[]) : [];
      } catch {
        return [];
      }
    })(),
  );
  const [pending, setPending] = useState<PendingFile[]>([]);
  const pendingRef = useRef<PendingFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const multiple = response.documentMultipleAllowed ?? true;
  const capture = response.documentCaptureMethod ?? 'both';
  const showDocument = capture === 'document' || capture === 'both';
  const showCamera = capture === 'camera' || capture === 'both';

  // Uploads attach to a persisted questionnaire. If the patient reaches a slot
  // before the first autosave, trigger one so an id exists.
  useEffect(() => {
    if (!questionnaireId && !persistRequested.current) {
      persistRequested.current = true;
      updateQuestionnaire();
    }
  }, [questionnaireId, updateQuestionnaire]);

  // The serialized value this component last emitted, so the round-trip through
  // the parent doesn't re-trigger the sync effect below.
  const lastEmittedRef = useRef<string | undefined>(value);

  const commit = (next: UploadedFile[]) => {
    filesRef.current = next;
    setFiles(next);
    const serialized = next.length ? JSON.stringify(next) : '';
    lastEmittedRef.current = serialized;
    onChange?.(serialized);
  };

  // Re-sync local state when the parent swaps `value` out from under us — e.g.
  // navigating back to a previously-answered question. Skip values we emitted
  // ourselves so our own commits don't round-trip and clobber the list.
  useEffect(() => {
    if (value === lastEmittedRef.current) return;
    lastEmittedRef.current = value;
    try {
      const parsed = value ? (JSON.parse(value) as UploadedFile[]) : [];
      filesRef.current = parsed;
      setFiles(parsed);
    } catch {
      filesRef.current = [];
      setFiles([]);
    }
  }, [value]);

  const handleFiles = async (
    selected: FileList | File[] | null,
    fromCamera: boolean,
  ) => {
    if (!selected || !questionnaireId) return;
    const list = Array.from(selected);
    if (!list.length) return;
    setError(null);

    const remainingSlots = multiple
      ? MAX_FILES_PER_SLOT - filesRef.current.length - pendingRef.current.length
      : 1;
    const chosen = list.slice(0, Math.max(remainingSlots, 0));
    if (!chosen.length) {
      setError(
        `Vous pouvez ajouter au maximum ${MAX_FILES_PER_SLOT} fichiers.`,
      );
      return;
    }

    // In single-file mode, capture the previous file id so we can best-effort
    // delete the old document after a successful replacement.
    const prevSingleId = !multiple ? filesRef.current[0]?.id : undefined;

    for (const file of chosen) {
      const sniffed = await sniffMimeType(file);
      if (!sniffed || !ALLOWED_MIME_TYPES.includes(sniffed)) {
        setError(
          'Format non pris en charge. Fichiers acceptés : PDF, JPEG, PNG.',
        );
        continue;
      }

      // Bound camera-captured images; never recompress user-selected files.
      let blob: Blob = file;
      let mimeType = sniffed;
      if (fromCamera && sniffed !== 'application/pdf') {
        blob = await boundImage(file);
        mimeType = 'image/jpeg';
      }

      if (blob.size > MAX_SIZE_BYTES) {
        setError('Chaque fichier doit faire moins de 15 Mo.');
        continue;
      }

      const tempId = crypto.randomUUID();
      const newPending = [...pendingRef.current, { tempId, name: file.name }];
      pendingRef.current = newPending;
      setPending(newPending);
      try {
        // Presign → PUT → confirm. The File row is only created at confirm
        // (after the server verifies the object exists), so a failed/abandoned
        // upload never leaves an orphan row.
        const baseBody = {
          questionnaireId,
          documentKey: response.documentKey,
          documentLabel: response.documentLabel,
          mimeType,
          originalName: file.name,
          sizeBytes: blob.size,
        };
        const body = {
          ...baseBody,
          documentCategory: response.documentCategory,
        };
        const { data: presign } = await client.post(
          '/questionnaire/document',
          body,
        );
        await axios.put(presign.url, blob, {
          headers: { 'Content-Type': mimeType },
        });
        const confirmBody = {
          ...baseBody,
          key: presign.key,
          ...(response.documentCategory !== undefined && {
            documentCategory: response.documentCategory,
          }),
          captureMethod: fromCamera ? 'camera' : 'file',
        };
        let confirmed;
        try {
          ({ data: confirmed } = await client.post(
            '/questionnaire/document/confirm',
            confirmBody,
          ));
        } catch (err) {
          // During a rollout where this patient bundle is live before the matching
          // API, the older API rejects the unknown `captureMethod` and
          // `documentCategory` fields (StrictValidationPipe forbidNonWhitelisted
          // -> 400). Retry once without them so the upload still succeeds;
          // capture-method analytics is best-effort.
          const status = (err as { response?: { status?: number } })?.response
            ?.status;
          if (status === 400) {
            ({ data: confirmed } = await client.post(
              '/questionnaire/document/confirm',
              { ...baseBody, key: presign.key },
            ));
          } else {
            throw err;
          }
        }
        const uploaded = { id: confirmed.id, name: file.name };
        commit(multiple ? [...filesRef.current, uploaded] : [uploaded]);
        // Single-file replace: best-effort delete the previous document to
        // avoid orphaning PHI in S3/DB. Errors are swallowed — the UI is
        // already showing the new file so the patient is never blocked.
        if (!multiple && prevSingleId && prevSingleId !== uploaded.id) {
          try {
            await client.delete(`/questionnaire/document/${prevSingleId}`);
          } catch {
            // Best-effort: ignore failures.
          }
        }
      } catch {
        setError('Le téléversement a échoué, veuillez réessayer');
      } finally {
        const afterPending = pendingRef.current.filter(
          (x) => x.tempId !== tempId,
        );
        pendingRef.current = afterPending;
        setPending(afterPending);
      }

      if (!multiple) break;
    }
  };

  const removeFile = async (id: string) => {
    try {
      await client.delete(`/questionnaire/document/${id}`);
    } catch {
      // Best-effort: drop it from the UI regardless so the patient isn't stuck.
    }
    commit(filesRef.current.filter((f) => f.id !== id));
  };

  const openPicker = () => {
    (showDocument ? documentInputRef : cameraInputRef).current?.click();
  };

  const hasContent = files.length > 0 || pending.length > 0;
  const canAddMore = multiple
    ? files.length + pending.length < MAX_FILES_PER_SLOT
    : files.length === 0 && pending.length === 0;
  const isEmpty = !hasContent;
  // First file: "Choisir un fichier"; once the list has content: "Ajouter…".
  const chooseLabel = isEmpty ? 'Choisir un fichier' : 'Ajouter un fichier';

  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    if (disabled) return;
    dragDepth.current += 1;
    setIsDragging(true);
  };
  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) {
      dragDepth.current = 0;
      setIsDragging(false);
    }
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragDepth.current = 0;
    setIsDragging(false);
    if (disabled || !canAddMore) return;
    handleFiles(e.dataTransfer.files, false);
  };

  // Desktop (≥640px / nsm): drag-and-drop is the primary affordance, so a single
  // plain text button. No camera (no webcam-capture flow on desktop), no icon.
  const desktopActions = (
    <div className="hidden items-center justify-center nsm:flex">
      {showDocument && (
        <button
          type="button"
          onClick={() => documentInputRef.current?.click()}
          disabled={!questionnaireId}
          className="inline-flex cursor-pointer items-center rounded-lg border border-indigo-300 bg-white px-4 py-2 text-sm font-medium text-indigo-700 transition-colors hover:bg-indigo-50 disabled:cursor-default disabled:opacity-50"
        >
          {chooseLabel}
        </button>
      )}
    </div>
  );

  // Mobile (<640px): no drag-and-drop, so the upload methods are the whole point.
  // Equal-size tiles with the icon stacked above the label. Tiles flex to share
  // the available width (capped at w-36) so two of them never overflow a narrow
  // phone; a lone method grows to that same w-36 cap and justify-center keeps it
  // centered. min-w-0 + wrapping labels let them shrink on the smallest screens.
  const mobileTileClass =
    'flex h-24 min-w-0 max-w-36 flex-1 cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-indigo-300 bg-white px-3 text-center text-xs font-medium text-indigo-700 transition-colors hover:bg-indigo-50 disabled:cursor-default disabled:opacity-50';
  const mobileActions = (
    <div className="flex w-full items-stretch justify-center gap-3 nsm:hidden">
      {showDocument && (
        <button
          type="button"
          onClick={() => documentInputRef.current?.click()}
          disabled={!questionnaireId}
          className={mobileTileClass}
        >
          <MdOutlineFileUpload className="size-6" />
          {chooseLabel}
        </button>
      )}
      {showCamera && (
        <button
          type="button"
          onClick={() => cameraInputRef.current?.click()}
          disabled={!questionnaireId}
          className={mobileTileClass}
        >
          <MdPhotoCamera className="size-6" />
          Prendre une photo
        </button>
      )}
    </div>
  );

  const actionRow = (
    <>
      {desktopActions}
      {mobileActions}
    </>
  );

  return (
    <div className="flex w-full flex-col gap-3">
      {/* The zone is a drag-and-drop target only; clicking opens the picker
          solely via the explicit buttons inside (Choisir/Photo/Remplacer). */}
      <div
        data-testid="dropzone"
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`relative w-full rounded-1.5xl border-2 border-dashed shadow-[0px_1.758px_7.031px_0px_rgba(0,0,0,0.06)] transition-colors ${
          isDragging
            ? 'border-blue-300 bg-blue-50/60'
            : 'border-gray-200 bg-white'
        }`}
      >
        <input
          ref={documentInputRef}
          type="file"
          className="hidden"
          accept="application/pdf,image/jpeg,image/png"
          multiple={multiple}
          disabled={!questionnaireId}
          onChange={(e) => {
            handleFiles(e.target.files, false);
            e.target.value = '';
          }}
        />
        <input
          ref={cameraInputRef}
          type="file"
          className="hidden"
          accept="image/jpeg,image/png"
          capture="environment"
          disabled={!questionnaireId}
          onChange={(e) => {
            handleFiles(e.target.files, true);
            e.target.value = '';
          }}
        />

        {response.label && (
          <p className="px-4 pt-3 text-base font-medium text-indigo-900">
            {parseInlineMarkdown(upperFirst(response.label))}
          </p>
        )}

        {isEmpty ? (
          <div className="flex min-h-44 flex-col items-center justify-center gap-3 px-6 py-8 text-center">
            {/* Badge accompanies the desktop drag-and-drop prompt only. */}
            <div className="hidden size-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 nsm:flex">
              <MdOutlineFileUpload className="size-6" />
            </div>
            {/* Drag-and-drop prompt is desktop-only — it doesn't work on mobile. */}
            <p className="hidden text-base font-medium text-indigo-900 nsm:block">
              {multiple
                ? 'Déposez vos fichiers ici'
                : 'Déposez votre fichier ici'}
            </p>
            {!disabled && actionRow}
            <div className="text-xs text-gray-400">
              <p>Fichiers PDF, JPEG ou PNG acceptés</p>
              <p>{multiple ? '15 Mo, 10 fichiers maximum' : '15 Mo maximum'}</p>
            </div>
          </div>
        ) : !multiple ? (
          <div className="flex min-h-44 flex-col items-center justify-center gap-3 px-6 py-8">
            <div className="w-full max-w-sm">
              {pending.length > 0 ? (
                <PendingCard name={pending[0].name} />
              ) : (
                <FileCard
                  name={files[0].name}
                  onRemove={
                    disabled ? undefined : () => removeFile(files[0].id)
                  }
                />
              )}
            </div>
            {!disabled && pending.length === 0 && (
              <button
                type="button"
                onClick={openPicker}
                className="cursor-pointer text-sm font-medium text-indigo-600 transition-colors hover:text-indigo-800 hover:underline"
              >
                Remplacer
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-2 p-3">
            <div className="flex flex-col gap-2">
              {files.map((file) => (
                <FileCard
                  key={file.id}
                  name={file.name}
                  onRemove={disabled ? undefined : () => removeFile(file.id)}
                />
              ))}
              {pending.map((p) => (
                <PendingCard key={p.tempId} name={p.name} />
              ))}
            </div>
            {!disabled && canAddMore && (
              <div className="flex items-center justify-center py-2">
                {actionRow}
              </div>
            )}
          </div>
        )}
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
};
